// ==============================================
// MyPolaris — Navigation Library
// ==============================================
//
// The user's personal cloud library of routes, tracks and waypoints:
// map-first (selection previews instantly over satellite imagery),
// GPX upload/download, snapshot-sharing with other Scout users, and a
// "Shared with me" shelf. Package-gated via packages.nav_library — the
// page renders an upsell card when the plan doesn't include it.
//
// Design per the approved concept artifact: Polaris-gold selection
// accent, type colors route/track/waypoint = blue/aqua/amber.

import { useMemo, useRef, useState, useEffect } from 'react';
import { useAuth } from '../../providers/AuthProvider';
import { ViewAsPicker, ViewAsBanner } from '../../components/nav/ViewAsPicker';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MapContainer, TileLayer, Polyline, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  ShareIcon,
  TrashIcon,
  PencilIcon,
  MapIcon,
  SparklesIcon,
  BookmarkIcon,
  PaperAirplaneIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';
import { BASEMAP_URLS } from '../../utils/constants';
import type { NavElement, NavType, BoatState } from '../../types/nav';
import { YourBoatCard, DraftFitBadge, useVessels, primaryVessel } from '../../components/nav/VesselProfile';
import {
  ViewportWatcher, FlyTo, GateDrawer, CrossingMarkers, PlaceSearch, useDiscovery,
  movedEnough, EMPTY_DISCOVERY, type Viewport, type LatLon as GeoLatLon,
} from '../../components/nav/Discover';
import { SharedLibrariesPanel, AddToLibraryModal } from '../../components/nav/SharedLibrariesPanel';
import {
  useChartEditor, ChartEditLayer, ChartContextMenu, PlacementOverlay, LegList, ConvertTrackModal,
  type MenuRequest,
} from '../../components/nav/ChartEditor';
import { toEditPoints, toWire, suggestSaveAsName, isHiddenByFilters, type EditPoint, type LatLon } from '../../utils/navEdit';
import { FrontDoorTileLayer } from '../../components/Map/FrontDoorTileLayer';

const TYPE_META: Record<NavType, { label: string; color: string }> = {
  route: { label: 'Route', color: '#3987e5' },
  track: { label: 'Track', color: '#2dd4a7' },
  waypoint: { label: 'Waypoint', color: '#f0c25a' },
};

const BASEMAPS = [
  { id: 'esri', label: 'Esri', url: BASEMAP_URLS.ESRI_SATELLITE, attribution: 'Esri World Imagery', source: 'esri-imagery' },
  { id: 'google', label: 'Google', url: BASEMAP_URLS.GOOGLE_SATELLITE, attribution: 'Google Satellite', source: null },
  { id: 'sentinel', label: 'Sentinel-2', url: BASEMAP_URLS.SENTINEL2, attribution: 'Sentinel-2 cloudless by EOX', source: 'sentinel2-cloudless' },
] as const;

function FitToSelection({ elements }: { elements: NavElement[] }) {
  const map = useMap();
  useEffect(() => {
    const pts = elements.flatMap((e) => e.points);
    if (!pts.length) return;
    const bounds = L.latLngBounds(pts.map(([lat, lon]) => L.latLng(lat, lon)));
    map.fitBounds(bounds.pad(0.25), { maxZoom: 14, animate: true });
  }, [map, elements]);
  return null;
}

/**
 * The point count, stated honestly.
 *
 * This used to read `original_point_count ?? point_count`, so a thinned track
 * advertised a count of points the row no longer held. Where the two differ,
 * say both: what the map is drawing, and what was recorded.
 */
function pointsLabel(e: NavElement): string {
  const stored = e.stats.point_count;
  const original = e.stats.original_point_count;
  if (e.type === 'route') return `${stored} waypoint${stored === 1 ? '' : 's'}`;
  if (original && original > stored) return `${stored.toLocaleString()} of ${original.toLocaleString()} pts`;
  return `${stored.toLocaleString()} pts`;
}

function metaLine(e: NavElement): string {
  const bits: string[] = [];
  if (e.stats.distance_nm !== undefined) bits.push(`${e.stats.distance_nm} nm`);
  bits.push(pointsLabel(e));
  if (e.stats.duration_h) bits.push(`${e.stats.duration_h} h`);
  if (e.createdAt) bits.push(new Date(e.createdAt).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' }));
  return bits.join(' · ');
}

/** Scouts holding a version older than the library's. */
const staleBoats = (e: NavElement) => (e.boats ?? []).filter((b) => b.state === 'stale');

const BOAT_STATE_META: Record<string, { dot: string; label: string; tone: string }> = {
  current: { dot: 'bg-emerald-400', label: 'current', tone: 'text-slate-300' },
  stale: { dot: 'bg-amber-400', label: 'older version', tone: 'text-amber-200' },
  queued: { dot: 'bg-sky-400 ring-2 ring-sky-400/30', label: 'queued', tone: 'text-sky-200' },
  not_sent: { dot: 'border border-slate-500', label: 'not sent', tone: 'text-slate-500' },
};

const shortDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' }) : null;

const daysSince = (iso: string | null) =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000) : null;

/** Attention first: an out-of-date boat matters more than an up-to-date one. */
const BOAT_STATE_ORDER: Record<string, number> = { stale: 0, queued: 1, current: 2, not_sent: 3 };

function BoatRow({ b, onSend }: { b: BoatState; onSend: (deviceId: string) => void }) {
  const meta = BOAT_STATE_META[b.state] ?? BOAT_STATE_META.not_sent;
  const sent = shortDate(b.delivered_at);
  const idle = b.state === 'queued' && !b.online ? daysSince(b.last_seen_at) : null;
  return (
    <div className="flex items-center gap-2 rounded-md bg-slate-800/60 px-2 py-1.5">
      <span className={`h-2 w-2 shrink-0 rounded-full ${meta.dot}`} aria-hidden />
      <span className={`min-w-0 flex-1 truncate text-[11px] ${meta.tone}`}>
        <b className="font-semibold text-white">{b.device_name ?? 'Scout'}</b>
        {' · '}{meta.label}
        {b.state === 'stale' && sent ? `, sent ${sent}` : ''}
        {idle !== null ? ` — last seen ${idle} day${idle === 1 ? '' : 's'} ago` : ''}
      </span>
      {(b.state === 'stale' || b.state === 'not_sent') && (
        <button
          type="button"
          onClick={() => onSend(b.device_id)}
          className="shrink-0 rounded border border-sky-500 px-1.5 py-0.5 text-[10px] font-semibold text-sky-300 hover:bg-sky-500/10"
        >{b.state === 'stale' ? 'Send update' : 'Send'}</button>
      )}
    </div>
  );
}

/**
 * What each Scout holds of this element. Deliberately on the detail card rather
 * than buried in a menu: after a Save the boat still holds the previous version
 * until someone presses Send, and a skipper who cannot see that will assume
 * otherwise.
 *
 * Boats that HOLD the route are always listed — that is the signal. Boats that
 * have never had it are folded into one line, because an account with a dozen
 * Scouts would otherwise bury the one that is out of date under a wall of
 * "not sent". Small fleets skip the fold; an extra click to see two boats is
 * worse than the wall it avoids.
 */
const FOLD_ABOVE = 4;

function BoatStrip({ boats, onSend }: { boats: BoatState[]; onSend: (deviceId: string) => void }) {
  const [showAll, setShowAll] = useState(false);
  if (!boats.length) return null;

  const sorted = [...boats].sort((a, b) =>
    (BOAT_STATE_ORDER[a.state] ?? 9) - (BOAT_STATE_ORDER[b.state] ?? 9)
    || (a.device_name ?? '').localeCompare(b.device_name ?? ''));

  const holding = sorted.filter((b) => b.state !== 'not_sent');
  const notSent = sorted.filter((b) => b.state === 'not_sent');
  const fold = !showAll && boats.length > FOLD_ABOVE && notSent.length > 1;
  const visible = fold ? holding : sorted;

  return (
    <div className="mb-3 space-y-1">
      <p className="text-[10px] uppercase tracking-wider text-slate-500">On your boats</p>
      {visible.map((b) => <BoatRow key={b.device_id} b={b} onSend={onSend} />)}
      {fold && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="w-full rounded-md border border-dashed border-slate-700 px-2 py-1.5 text-left text-[11px] text-slate-500 hover:border-slate-500 hover:text-slate-300"
        >
          {holding.length
            ? `Not on ${notSent.length} other boat${notSent.length === 1 ? '' : 's'}`
            : `Not on any of your ${notSent.length} boats`} — show
        </button>
      )}
      {showAll && boats.length > FOLD_ABOVE && (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          className="w-full px-2 py-1 text-left text-[11px] text-slate-500 hover:text-slate-300"
        >Show fewer</button>
      )}
    </div>
  );
}

export function NavigationLibraryPage() {
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);
  const fileRef = useRef<HTMLInputElement>(null);
  const { data: vessels } = useVessels();
  const myBoat = primaryVessel(vessels);

  // ?lib=<id> (from notification emails and admin) opens that shared library directly.
  const [searchParams] = useSearchParams();
  const deepLinkLib = searchParams.get('lib');
  const [scope, setScope] = useState<'mine' | 'shared' | 'libraries' | 'discover'>(deepLinkLib ? 'libraries' : 'mine');
  // Shared libraries: items previewed from a library, and the add-to-library picker.
  const [libPreview, setLibPreview] = useState<NavElement[]>([]);
  const onLibPreview = useCallback((els: NavElement[]) => setLibPreview(els), []);
  const [addToLib, setAddToLib] = useState<NavElement[] | null>(null);
  const [types, setTypes] = useState<Set<NavType>>(new Set(['route', 'track', 'waypoint']));
  const [staleOnly, setStaleOnly] = useState(false);
  const [boatCardOpen, setBoatCardOpen] = useState(false);
  // Discovery: the map is the query.
  const discovery = useDiscovery();
  const [viewport, setViewport] = useState<Viewport | null>(null);
  const [searchedAt, setSearchedAt] = useState<Viewport | null>(null);
  const [flyTarget, setFlyTarget] = useState<{ lat: number; lon: number; spanDeg: number } | null>(null);
  const [gateMode, setGateMode] = useState(false);
  const [gate, setGate] = useState<[GeoLatLon, GeoLatLon] | null>(null);
  /** Hovering a result highlights it on the map, and vice versa. */
  const [hoverId, setHoverId] = useState<string | null>(null);
  // Filter shared work down to what a boat of my draft has actually been through.
  const [fitsMyDraft, setFitsMyDraft] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [basemap, setBasemap] = useState<(typeof BASEMAPS)[number]>(BASEMAPS[0]);
  const [shareTarget, setShareTarget] = useState<NavElement | null>(null);
  const [shareEmail, setShareEmail] = useState('');
  const [sendTarget, setSendTarget] = useState<NavElement | null>(null);
  const [sendDeviceId, setSendDeviceId] = useState<string | null>(null);
  // Rename/Delete use in-app modals, not window.prompt/confirm — browsers
  // (notably managed Chrome profiles) suppress native dialogs, which made
  // both buttons silently do nothing.
  const [renameTarget, setRenameTarget] = useState<NavElement | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<NavElement | null>(null);
  // Action card roll-up — clears the imagery when needed. Always re-expands
  // for a newly selected item.
  const [cardCollapsed, setCardCollapsed] = useState(false);
  // Chart editing: the map becomes an editor for one route or waypoint at a time.
  const [menu, setMenu] = useState<MenuRequest | null>(null);
  const [saveAsName, setSaveAsName] = useState<string | null>(null);
  const [wptRename, setWptRename] = useState<{ index: number; value: string } | null>(null);
  const [convertTrack, setConvertTrack] = useState<NavElement | null>(null);
  const [discardPrompt, setDiscardPrompt] = useState(false);
  // The editing panel rolls up the same way the action card does — editing
  // happens on the water underneath it, so it has to be able to get out of
  // the way. The controls stay put; only the readout folds.
  const [editCollapsed, setEditCollapsed] = useState(false);

  const { data: access, isLoading: accessLoading } = useQuery({
    queryKey: ['nav-access'],
    queryFn: () => api.navAccess(),
  });

  // ---- super-admin "view as" ----
  // The subject lives on the api client (a request interceptor attaches it to
  // /api/nav calls), so it is set there rather than threaded through the many
  // nav queries on this page. Switching customer then invalidates everything
  // cached, because that cache belongs to whoever we were a moment ago —
  // showing one customer's routes under another's name would be the worst
  // possible failure of a feature like this.
  const { user: signedInUser } = useAuth();
  const [viewAsUser, setViewAsUser] = useState<{ id: string; name?: string | null; email: string } | null>(null);

  useEffect(() => {
    api.setViewAs(viewAsUser?.id ?? null);
    // Anything already fetched belongs to whoever we were before.
    queryClient.invalidateQueries();
    // Leaving the page must not leave the client impersonating someone.
    return () => { api.setViewAs(null); };
  }, [viewAsUser, queryClient]);


  const { data: items, isLoading } = useQuery({
    queryKey: ['nav-list', scope],
    queryFn: () => api.navList(scope as 'mine' | 'shared'),
    enabled: access?.enabled === true && scope !== 'libraries',
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['nav-list'] });

  /**
   * Put a freshly created element on screen, now.
   *
   * `invalidate()` alone was not enough: it fires a refetch whose result lands
   * some time later, and it cannot help at all if a type chip or the search box
   * would have filtered the new item out. Either way the user creates something
   * and sees nothing, which is indistinguishable from the save having failed.
   *
   * So seed the cache with the row the API just handed back, relax only the
   * filter that would have hidden it, select it, and let the refetch reconcile.
   */
  const revealCreated = useCallback((el: NavElement) => {
    queryClient.setQueryData<NavElement[]>(['nav-list', 'mine'], (old) =>
      (old ? [el, ...old.filter((e) => e.id !== el.id)] : [el]));
    setScope('mine');
    setTypes((prev) => (prev.has(el.type) ? prev : new Set(prev).add(el.type)));
    // Type is already handled above, so this asks only "would the search box
    // hide it?" — hence a type set that cannot be the reason.
    setQ((cur) => (isHiddenByFilters(el, new Set([el.type]), cur) ? '' : cur));
    setSelected(new Set([el.id]));
    void invalidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  // The chart editor. Save overwrites in place; Save As and a drawn element
  // both go through /draw, which is also what a converted track uses.
  const editor = useChartEditor({
    onSave: async (points: EditPoint[]) => {
      const target = editorTargetRef.current;
      if (!target) return;
      const wire = toWire(points);
      const isRoute = target.type === 'route';
      const updated = await api.navSaveGeometry(target.id, wire.points, isRoute ? wire.waypoint_names : null);
      // A standalone waypoint's label IS its element name — the geometry call
      // carries no names for it, so renaming one on the chart has to rename the
      // element or the change is silently thrown away on save.
      const renamed = isRoute ? null : wire.waypoint_names[0]?.trim();
      if (renamed && renamed !== target.name) {
        await api.navUpdate(target.id, { name: renamed });
        updated.name = renamed;
      }
      queryClient.setQueryData<NavElement[]>(['nav-list', 'mine'], (old) =>
        old?.map((e) => (e.id === updated.id ? updated : e)));
      void invalidate();

      // Save writes to the library, not to the boat. Any Scout still holding the
      // previous version is named here with a one-tap way to reconcile — never
      // pushed automatically, because a chart that rearranges itself under a
      // skipper who is underway is the wrong kind of surprise.
      const stale = staleBoats(updated);
      if (stale.length) {
        const [first] = stale;
        const who = stale.length === 1
          ? (first.device_name ?? 'Your Scout')
          : `${stale.length} of your Scouts`;
        const sent = shortDate(first.delivered_at);
        addToast({
          type: 'warning',
          title: 'Saved to your library',
          message: stale.length === 1 && sent
            ? `${who} still has the version from ${sent}.`
            : `${who} still ${stale.length === 1 ? 'has' : 'have'} an older version.`,
          action: stale.length === 1
            ? {
              label: `Send to ${first.device_name ?? 'boat'}`,
              onClick: () => sendToBoat.mutate({ id: updated.id, deviceId: first.device_id }),
            }
            : undefined,
        });
      } else {
        addToast({ type: 'success', title: 'Saved to your library', message: `“${target.name}” updated` });
      }
    },
    onSaveAs: async (name: string, points: EditPoint[], sourceId: string | null) => {
      const target = editorTargetRef.current;
      const wire = toWire(points);
      const created = await api.navDraw({
        type: target?.type === 'waypoint' ? 'waypoint' : 'route',
        name, points: wire.points,
        waypointNames: target?.type === 'waypoint' ? null : wire.waypoint_names,
        sourceId,
      });
      revealCreated(created);
      addToast({ type: 'success', title: 'Saved as new', message: `“${name}” is in My library` });
    },
    onDraw: async (type, name, points, sourceId) => {
      const wire = toWire(points);
      const created = await api.navDraw({
        type, name, points: wire.points,
        waypointNames: type === 'route' ? wire.waypoint_names : null,
        sourceId,
      });
      revealCreated(created);
      addToast({ type: 'success', title: type === 'route' ? 'Route created' : 'Waypoint created', message: name });
    },
  });
  // The mutation callbacks above close over the element being edited; a ref
  // keeps them reading the current one without re-creating the whole editor.
  const editorTargetRef = useRef<NavElement | null>(null);
  editorTargetRef.current = editor.state.target;

  // Send to Boat targets — the account's Scouts, with a liveness hint.
  const { data: devicesData } = useQuery({
    queryKey: ['my-devices'],
    queryFn: () => api.getMyDevices(),
    enabled: sendTarget != null,
  });
  const myDevices = devicesData?.devices ?? [];
  const isOnline = (lastSeen: string | Date | null | undefined) =>
    lastSeen != null && Date.now() - new Date(lastSeen).getTime() < 10 * 60_000;

  const upload = useMutation({
    mutationFn: ({ gpx, name }: { gpx: string; name: string }) => api.navUpload(gpx, name),
    onSuccess: () => { invalidate(); addToast({ type: 'success', title: 'GPX imported', message: 'Elements filed into your library' }); },
  });
  const share = useMutation({
    mutationFn: ({ id, email }: { id: string; email: string }) => api.navShare(id, email),
    onSuccess: (r) => { setShareTarget(null); setShareEmail(''); addToast({ type: 'success', title: 'Shared', message: `Now in ${r.to}’s “Shared with me”` }); },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.navDelete(id),
    onSuccess: () => { invalidate(); setSelected(new Set()); setDeleteTarget(null); addToast({ type: 'success', title: 'Deleted' }); },
  });
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.navUpdate(id, { name }),
    onSuccess: () => { invalidate(); setRenameTarget(null); addToast({ type: 'success', title: 'Renamed' }); },
  });
  const sendToBoat = useMutation({
    mutationFn: ({ id, deviceId }: { id: string; deviceId: string }) => api.navSendToBoat(id, deviceId),
    onSuccess: (r) => {
      setSendTarget(null); setSendDeviceId(null);
      addToast({
        type: 'success',
        title: r.online ? `Sent to ${r.device_name ?? 'your Scout'}` : `Queued for ${r.device_name ?? 'your Scout'}`,
        message: r.online
          ? 'It will appear in the Scout’s library shortly — hidden until the skipper shows it.'
          : 'She’s offline — it delivers automatically the next time she checks in.',
      });
    },
  });
  const saveCopy = useMutation({
    mutationFn: (id: string) => api.navSaveCopy(id),
    onSuccess: () => { invalidate(); addToast({ type: 'success', title: 'Saved', message: 'Copied into your library' }); },
  });

  // Elements a Scout holds an older version of. Surfaced as a count so drift is
  // discoverable across the whole library, not one item at a time.
  const staleCount = useMemo(
    () => (items ?? []).filter((e) => staleBoats(e).length > 0).length,
    [items],
  );

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (items ?? [])
      .filter((e) => types.has(e.type))
      .filter((e) => !staleOnly || staleBoats(e).length > 0)
      .filter((e) => !fitsMyDraft || e.draftFit?.verdict === 'deeper_sailed')
      .filter((e) => !needle || e.name.toLowerCase().includes(needle) || e.tags.some((t) => t.toLowerCase().includes(needle)));
  }, [items, types, q, staleOnly, fitsMyDraft]);

  // An element can be edited in place only if the user authored it. A 'share'
  // copy still carries someone else's attribution and a 'library' copy tracks
  // its curator's original, so both go to Save As instead — matching the API.
  const canOverwrite = (e: NavElement) => e.origin !== 'share' && e.origin !== 'library';
  const isEditable = (e: NavElement) => e.type !== 'track';

  const beginEdit = useCallback((el: NavElement) => {
    if (!isEditable(el)) return;
    editor.begin(el, toEditPoints(el.points, el.stats.waypoint_names, el.name, el.type));
    setMenu(null);
  }, [editor]);

  // Ctrl/Cmd+Z and Ctrl/Cmd+Shift+Z drive the session history.
  useEffect(() => {
    if (editor.state.mode !== 'edit') return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z') return;
      const el = document.activeElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return;
      e.preventDefault();
      if (e.shiftKey) editor.redo(); else editor.undo();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editor]);

  const selectedElements = useMemo(() => (scope === 'libraries' ? libPreview : visible.filter((e) => selected.has(e.id))), [scope, libPreview, visible, selected]);
  const single = selectedElements.length === 1 ? selectedElements[0] : null;
  useEffect(() => { setCardCollapsed(false); }, [single?.id]);
  // Same rule for the editing panel: it keeps whatever the user set for the
  // duration of a session, and starts fresh on the next element.
  useEffect(() => { setEditCollapsed(false); }, [editor.state.target?.id]);

  const toggleSelect = (id: string, additive: boolean) => {
    setSelected((prev) => {
      if (additive) {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      }
      return prev.has(id) && prev.size === 1 ? new Set() : new Set([id]);
    });
  };

  const downloadSelection = async (fidelity: 'simplified' | 'original' = 'simplified') => {
    if (!selectedElements.length) return;
    const { gpx, degraded } = await api.navDownload(selectedElements.map((e) => e.id), fidelity);
    const stem = single ? single.name.replace(/[^\w]+/g, '-').toLowerCase() : 'mypolaris-selection';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([gpx], { type: 'application/gpx+xml' }));
    a.download = fidelity === 'original' ? `${stem}-original.gpx` : `${stem}.gpx`;
    a.click();
    if (degraded > 0) {
      addToast({
        type: 'warning',
        title: 'Some originals were unavailable',
        message: `${degraded} item${degraded === 1 ? '' : 's'} downloaded at map resolution because the archived recording could not be read.`,
      });
    }
  };

  /** Selection contains something whose full recording is archived. */
  const hasArchivedOriginal = selectedElements.some((e) => e.gpxFullKey);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    const text = await file.text();
    upload.mutate({ gpx: text, name: file.name.replace(/\.gpx$/i, '') });
  };

  // ---- gate states ----
  if (accessLoading) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  if (access && !access.enabled) {
    return (
      <div className="p-6 max-w-xl mx-auto mt-10">
        <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-8 text-center">
          <SparklesIcon className="w-10 h-10 mx-auto text-amber-400 mb-3" />
          <h1 className="text-xl font-bold text-white mb-2">Navigation Library</h1>
          <p className="text-slate-300 mb-1">Keep every route, track and waypoint from your Scout safe in your MyPolaris account — manage them, share them with other Scout users, and view them over satellite imagery.</p>
          <p className="text-slate-400 text-sm mb-6">The Navigation Library isn’t included in your current plan.</p>
          <a href="/dashboard/billing" className="inline-block px-5 py-2.5 rounded-lg bg-amber-400 text-slate-900 font-bold hover:brightness-105">
            See plans that include it
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      {/* header */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <MapIcon className="w-6 h-6 text-amber-400" /> Navigation Library
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">Routes, tracks and waypoints from your boat — managed, shared, and safe.</p>
        </div>
        <span className="flex-1" />
        {/*
          Since 2026-09-11 this page is the FALLBACK, reachable only at
          /dashboard/library/classic; the menu and every old link now go to
          /library — the PassageWiki app wearing MyPolaris's clothes, one
          codebase, two doors. Both read the same library, so nothing is at risk
          in moving between them. This notice goes when this page does.
        */}
        <a href="/library" className="inline-flex items-center gap-1.5 rounded-lg border border-amber-400/60 bg-amber-400/10 px-3 py-1.5 text-sm font-semibold text-amber-200 hover:border-amber-400"
          title="Collections, sharing states, weather and the ENC chart. Your passages are the same in both.">
          You are on the previous version — open the current library
        </a>
        {scope === 'mine' && selectedElements.length > 0 && (
          <button onClick={() => setAddToLib(selectedElements)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-600 bg-slate-800 text-sm text-white hover:border-amber-400">
            <BookmarkIcon className="w-4 h-4" /> Add to shared library{selectedElements.length > 1 ? ` (${selectedElements.length})` : ''}
          </button>
        )}
        {scope === 'mine' && !editor.isEditing && (
          <>
            <button onClick={() => editor.startDraw('route')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-sky-500 px-3 py-1.5 text-sm font-semibold text-sky-300 hover:bg-sky-500/10">
              <MapIcon className="h-4 w-4" /> New route
            </button>
            <button onClick={() => editor.startDraw('waypoint')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-400 px-3 py-1.5 text-sm font-semibold text-amber-300 hover:bg-amber-400/10">
              <SparklesIcon className="h-4 w-4" /> New waypoint
            </button>
          </>
        )}
        <button onClick={() => void downloadSelection('simplified')} disabled={!selectedElements.length}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-600 bg-slate-800 text-sm text-white hover:border-amber-400 disabled:opacity-40">
          <ArrowDownTrayIcon className="w-4 h-4" /> Download GPX{selectedElements.length > 1 ? ` (${selectedElements.length})` : ''}
        </button>
        {/* Only offered when there is genuinely a fuller version to hand over. */}
        {hasArchivedOriginal && (
          <button onClick={() => void downloadSelection('original')}
            title="The full-resolution recording as it came off the boat"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-500/60 bg-slate-800 text-sm text-emerald-300 hover:border-emerald-400">
            <ArrowDownTrayIcon className="w-4 h-4" /> Original recording
          </button>
        )}
        <button onClick={() => setBoatCardOpen(true)}
          title={myBoat?.hasDraft ? 'Your boat' : 'Add your boat so shared passages can be compared with your draft'}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm ${
            myBoat?.hasDraft
              ? 'border-slate-600 bg-slate-800 text-white hover:border-amber-400'
              : 'border-amber-400/60 bg-amber-400/10 text-amber-200 hover:border-amber-400'
          }`}>
          {myBoat?.hasDraft
            ? <>⌀ {myBoat.draftM?.toFixed(2)} m</>
            : <>Add your boat</>}
        </button>
        <button onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-400 text-slate-900 text-sm font-bold hover:brightness-105">
          <ArrowUpTrayIcon className="w-4 h-4" /> Upload GPX
        </button>
        <input ref={fileRef} type="file" accept=".gpx,application/gpx+xml" className="hidden"
          onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
        <ViewAsPicker
          role={signedInUser?.role}
          viewAs={viewAsUser}
          onChange={setViewAsUser}
        />
      </div>

      <ViewAsBanner viewAs={viewAsUser} onExit={() => setViewAsUser(null)} />

      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex bg-slate-800 border border-slate-600 rounded-lg p-0.5">
          {(['mine', 'shared', 'libraries', 'discover'] as const).map((s) => (
            <button key={s} onClick={() => { setScope(s); setSelected(new Set()); }} aria-pressed={scope === s}
              className={`px-3 py-1 rounded-md text-sm font-semibold ${scope === s ? 'bg-amber-400 text-slate-900' : 'text-slate-400 hover:text-white'}`}>
              {s === 'mine' ? 'My library' : s === 'shared' ? 'Shared with me' : s === 'libraries' ? 'Shared libraries' : 'Discover'}
            </button>
          ))}
        </div>
        {(Object.keys(TYPE_META) as NavType[]).map((t) => (
          <button key={t} onClick={() => setTypes((prev) => {
            if (prev.has(t) && prev.size === 1) return prev;
            const next = new Set(prev);
            next.has(t) ? next.delete(t) : next.add(t);
            return next;
          })} aria-pressed={types.has(t)}
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium ${types.has(t) ? 'border-slate-500 bg-slate-800 text-white' : 'border-slate-700 bg-slate-800/50 text-slate-500'}`}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: types.has(t) ? TYPE_META[t].color : '#64748b' }} />
            {TYPE_META[t].label}s
          </button>
        ))}
        {scope === 'mine' && staleCount > 0 && (
          <button
            type="button"
            onClick={() => setStaleOnly((v) => !v)}
            aria-pressed={staleOnly}
            title="Items a Scout still holds an older version of"
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium ${
              staleOnly
                ? 'border-amber-400 bg-amber-400/15 text-amber-200'
                : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:text-amber-200'
            }`}
          >
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" aria-hidden />
            Out of date on boat · {staleCount}
          </button>
        )}
        {scope === 'discover' && (<>
          <PlaceSearch onPick={(p) => {
            if (p.centroidLat == null || p.centroidLon == null) return;
            setFlyTarget({ lat: p.centroidLat, lon: p.centroidLon, spanDeg: p.spanDeg });
          }} />
          <button
            type="button"
            disabled={!viewport || discovery.searchArea.isPending}
            onClick={() => {
              if (!viewport) return;
              discovery.searchArea.mutate({ viewport, q: q.trim() || undefined, types: [...types] });
              setSearchedAt(viewport);
              setGate(null);
            }}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-bold disabled:opacity-40 ${
              movedEnough(searchedAt, viewport ?? { west: 0, south: 0, east: 0, north: 0 })
                ? 'bg-amber-400 text-slate-900'
                : 'border border-slate-600 bg-slate-800 text-white'
            }`}
          >
            {discovery.searchArea.isPending ? 'Searching…' : 'Search this area'}
          </button>
          <button
            type="button"
            onClick={() => { setGateMode((v) => !v); if (gateMode) setGate(null); }}
            aria-pressed={gateMode}
            title="Drag a line across a pass and see which passages crossed it"
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm ${
              gateMode ? 'border-amber-400 bg-amber-400/15 text-amber-200' : 'border-slate-600 bg-slate-800 text-white hover:border-amber-400'
            }`}
          >
            {gateMode ? 'Click two points…' : 'Who crossed here?'}
          </button>
          {(gate || discovery.state.mode === 'crossing') && (
            <button
              type="button"
              onClick={() => { setGate(null); setGateMode(false); discovery.setState(EMPTY_DISCOVERY); }}
              className="rounded-lg border border-slate-700 px-2.5 py-1.5 text-sm text-slate-400 hover:text-white"
            >Clear</button>
          )}
        </>)}
        {scope !== 'mine' && scope !== 'discover' && myBoat?.hasDraft && (
          <button
            type="button"
            onClick={() => setFitsMyDraft((v) => !v)}
            aria-pressed={fitsMyDraft}
            title={`Only passages sailed by a boat drawing at least ${myBoat.draftM?.toFixed(2)} m`}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium ${
              fitsMyDraft
                ? 'border-emerald-400 bg-emerald-400/15 text-emerald-200'
                : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:text-emerald-200'
            }`}
          >
            <span aria-hidden className="font-mono text-[11px]">✓</span>
            Sailed by my draft or deeper
          </button>
        )}
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search names or tags…"
          className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white w-56 placeholder:text-slate-500" />
      </div>

      {/* split */}
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
        {/* list */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden flex flex-col max-h-[640px]">
          {scope === 'libraries' ? (
            <SharedLibrariesPanel onPreview={onLibPreview} initialLibraryId={deepLinkLib} />
          ) : scope === 'discover' ? (<>
            <div className="border-b border-slate-700/60 px-4 py-2.5">
              <div className="flex items-baseline">
                <b className="text-sm text-white">
                  {discovery.state.mode === 'crossing' ? 'Crossing this line' : 'In this view'}
                </b>
                <span className="ml-auto text-xs tabular-nums text-slate-500">
                  {discovery.state.total} {discovery.state.total === 1 ? 'passage' : 'passages'}
                  {discovery.state.truncated ? ' (showing the first few)' : ''}
                </span>
              </div>
              <p className="mt-0.5 text-[11px] leading-relaxed text-slate-500">
                {discovery.state.mode === 'crossing'
                  ? 'Every passage on record that crossed the line you drew.'
                  : 'Your library and every shared library you have joined, wherever the map is pointing.'}
              </p>
            </div>
            <div className="flex-1 space-y-1 overflow-y-auto p-2">
              {(discovery.searchArea.isPending || discovery.searchCrossing.isPending) ? (
                <div className="p-8 text-center text-sm text-slate-400">Searching…</div>
              ) : !discovery.state.results.length ? (
                <div className="p-8 text-center text-sm text-slate-500">
                  {discovery.state.mode === 'crossing'
                    ? 'Nothing on record has crossed that line. Empty water is worth knowing about too.'
                    : 'Move the map to somewhere you are interested in, then press Search this area.'}
                </div>
              ) : (
                discovery.state.results.map((e) => (
                  <button
                    key={e.id} type="button"
                    onClick={() => setSelected(new Set([e.id]))}
                    onMouseEnter={() => setHoverId(e.id)}
                    onMouseLeave={() => setHoverId((cur) => (cur === e.id ? null : cur))}
                    className={`w-full rounded-lg px-3 py-2 text-left ${
                      selected.has(e.id) ? 'bg-amber-400/10 ring-1 ring-amber-400/40' : 'hover:bg-slate-700/40'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: TYPE_META[e.type].color }} />
                      <span className="truncate font-semibold text-white">{e.name}</span>
                    </div>
                    <div className="mt-0.5 pl-[18px] text-xs tabular-nums text-slate-400">{metaLine(e)}</div>
                    {e.sharedByName && (
                      <div className="pl-[18px] text-[11px] text-slate-500">from {e.sharedByName}</div>
                    )}
                    {e.draftFit && (
                      <div className="mt-1 pl-[18px]"><DraftFitBadge fit={e.draftFit} compact /></div>
                    )}
                  </button>
                ))
              )}
            </div>
          </>) : (<>
          <div className="px-4 py-2.5 flex items-baseline border-b border-slate-700/60">
            <b className="text-sm text-white">{scope === 'mine' ? 'My library' : 'Shared with me'}</b>
            <span className="ml-auto text-xs text-slate-500 tabular-nums">{visible.length} items</span>
          </div>
          <div className="overflow-y-auto flex-1 p-2 space-y-1">
            {isLoading ? (
              <div className="p-8 text-center text-slate-400 text-sm">Loading library…</div>
            ) : visible.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                {scope === 'shared' ? 'Nothing shared with you yet.' : 'Your library is empty — upload a GPX file to get started. Sync from your Scout is coming next.'}
              </div>
            ) : (
              visible.map((e) => (
                <button key={e.id} onClick={(ev) => toggleSelect(e.id, ev.ctrlKey || ev.metaKey || ev.shiftKey)}
                  className={`w-full text-left rounded-lg px-3 py-2 border ${selected.has(e.id) ? 'border-amber-400 bg-amber-400/5' : 'border-transparent hover:bg-slate-700/40'}`}>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: TYPE_META[e.type].color }} />
                    <span className="font-semibold text-sm text-white truncate">{e.name}</span>
                    {e.sharedByName && (scope === 'shared' || e.origin === 'library') && (
                      <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-amber-400 border border-amber-400/50 rounded px-1.5 py-0.5 shrink-0">
                        from {e.sharedByName.split(' ')[0]}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5 pl-[18px] tabular-nums">{metaLine(e)}</div>
                </button>
              ))
            )}
          </div>
          </>)}
        </div>

        {/* map */}
        <div className="relative bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden min-h-[520px]">
          <div className="absolute top-3 left-3 z-[1000] flex gap-1 bg-slate-900/85 border border-slate-600 rounded-full p-1">
            {BASEMAPS.map((b) => (
              <button key={b.id} onClick={() => setBasemap(b)} aria-pressed={basemap.id === b.id}
                className={`px-3 py-1 rounded-full text-xs font-bold ${basemap.id === b.id ? 'bg-amber-400 text-slate-900' : 'text-slate-300 hover:text-white'}`}>
                {b.label}
              </button>
            ))}
          </div>

          <MapContainer center={[-36.6, 174.9]} zoom={9} className="absolute inset-0" style={{ background: '#0a1220' }} zoomControl={false}>
            {basemap.source
              ? <FrontDoorTileLayer key={basemap.id} source={basemap.source} fallbackUrl={basemap.url} attribution={basemap.attribution} maxZoom={19} />
              : <TileLayer key={basemap.id} url={basemap.url} attribution={basemap.attribution} maxZoom={19} />}
            {scope === 'discover' && (<>
              <ViewportWatcher onMove={setViewport} />
              <FlyTo target={flyTarget} />
              <GateDrawer
                active={gateMode}
                gate={gate}
                onGate={(g) => { setGate(g); setGateMode(false); discovery.searchCrossing.mutate(g); }}
                onCancel={() => setGateMode(false)}
              />
              <CrossingMarkers points={discovery.state.crossingAt} />
              {/* Results draw on the map, hover-linked to the list both ways. */}
              {discovery.state.results.filter((e) => !selected.has(e.id)).map((e) => (
                e.type === 'waypoint' ? (
                  <CircleMarker
                    key={`d-${e.id}`} center={e.points[0]} radius={hoverId === e.id ? 9 : 5}
                    pathOptions={{ color: TYPE_META.waypoint.color, weight: 2, fillOpacity: hoverId === e.id ? 0.7 : 0.3 }}
                    eventHandlers={{
                      mouseover: () => setHoverId(e.id),
                      mouseout: () => setHoverId((c) => (c === e.id ? null : c)),
                      click: () => setSelected(new Set([e.id])),
                    }}
                  ><Tooltip sticky>{e.name}</Tooltip></CircleMarker>
                ) : (
                  <Polyline
                    key={`d-${e.id}`} positions={e.points}
                    pathOptions={{
                      color: TYPE_META[e.type].color,
                      weight: hoverId === e.id ? 5 : 2,
                      opacity: hoverId === e.id ? 1 : 0.55,
                      lineCap: 'round',
                    }}
                    eventHandlers={{
                      mouseover: () => setHoverId(e.id),
                      mouseout: () => setHoverId((c) => (c === e.id ? null : c)),
                      click: () => setSelected(new Set([e.id])),
                    }}
                  ><Tooltip sticky>{e.name}</Tooltip></Polyline>
                )
              ))}
            </>)}
            {selectedElements.filter((e) => e.id !== editor.state.target?.id).map((e) =>
              e.type === 'waypoint' ? (
                <CircleMarker key={e.id} center={e.points[0]} radius={9}
                  pathOptions={{ color: TYPE_META.waypoint.color, weight: 3, fillColor: TYPE_META.waypoint.color, fillOpacity: 0.35 }}>
                  <Tooltip permanent direction="right" offset={[10, 0]}>{e.name}</Tooltip>
                </CircleMarker>
              ) : (
                <Polyline key={e.id} positions={e.points}
                  pathOptions={{ color: TYPE_META[e.type].color, weight: e.type === 'route' ? 4 : 3, dashArray: e.type === 'track' ? '1 8' : undefined, lineCap: 'round' }}>
                  <Tooltip sticky>{e.name}</Tooltip>
                </Polyline>
              ),
            )}
            {/* A route is one entity WITH its waypoints — draw them, named, on the line.
                Labels stay pinned for short routes; long routes show them on hover. */}
            {selectedElements.filter((e) => e.type === 'route' && e.id !== editor.state.target?.id).map((e) =>
              e.points.map((pt, i) => {
                const label = e.stats.waypoint_names?.[i] || `WP${i + 1}`;
                return (
                  <CircleMarker key={`${e.id}-wp-${i}`} center={pt} radius={6}
                    pathOptions={{ color: '#0f172a', weight: 2, fillColor: TYPE_META.waypoint.color, fillOpacity: 1 }}>
                    <Tooltip permanent={e.points.length <= 12} direction="top" offset={[0, -6]}>{label}</Tooltip>
                  </CircleMarker>
                );
              }),
            )}
            {/* Editing freezes the auto-fit: the map must not fly away mid-drag. */}
            {!editor.isEditing && <FitToSelection elements={selectedElements} />}
            {editor.isEditing && <ChartEditLayer editor={editor} onMenu={setMenu} />}
          </MapContainer>

          {editor.isEditing && menu && (
            <ChartContextMenu
              menu={menu}
              editor={editor}
              onClose={() => setMenu(null)}
              onRename={(index) => setWptRename({ index, value: editor.state.points[index]?.name ?? '' })}
            />
          )}
          {editor.isPlacing && <PlacementOverlay editor={editor} />}

          {/* Editing panel — replaces the action card while a session is open. */}
          {editor.isEditing && editor.state.mode === 'edit' && (
            <div className={`absolute top-3 right-3 z-[1000] w-72 rounded-xl border border-sky-500/60 bg-slate-900 shadow-2xl ${editCollapsed ? 'px-4 py-2.5' : 'p-4'}`}>
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <h3 className="min-w-0 flex-1 truncate font-bold text-white">
                      {editor.state.target?.name ?? 'Editing'}
                    </h3>
                    {editor.state.dirty && <span className="shrink-0 text-[10px] font-semibold text-amber-400">● UNSAVED</span>}
                  </div>
                  <p className="text-xs text-slate-400">
                    {editor.state.points.length} point{editor.state.points.length === 1 ? '' : 's'}
                    {editor.state.target?.type === 'route' && ` · ${editor.stats.nm.toFixed(2)} nm`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditCollapsed((v) => !v)}
                  aria-label={editCollapsed ? 'Expand waypoint list' : 'Collapse waypoint list'}
                  aria-expanded={!editCollapsed}
                  className="-mr-1 shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-700/60 hover:text-white"
                >
                  {editCollapsed ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronUpIcon className="h-4 w-4" />}
                </button>
              </div>

              {!editCollapsed && (<>
                {editor.state.target && !canOverwrite(editor.state.target) && (
                  <p className="mb-3 mt-3 rounded-md border-l-2 border-amber-400 bg-amber-400/10 px-2.5 py-2 text-[11px] leading-relaxed text-amber-200">
                    This came from {editor.state.target.sharedByName ?? 'another skipper'}. Edit freely —
                    <b> Save As</b> puts your version in My library.
                  </p>
                )}

                <div className="mb-3 mt-3">
                  <LegList
                    editor={editor}
                    onRename={(index) => setWptRename({ index, value: editor.state.points[index]?.name ?? '' })}
                  />
                </div>

                <p className="mb-2 text-[11px] leading-relaxed text-slate-500">
                  Drag a point to move it · hover a leg for its midpoint ·
                  ✎ or double-click a row to rename · right-click for the full menu
                </p>
              </>)}

              <div className={`flex flex-wrap gap-1.5 ${editCollapsed ? 'mt-2.5' : ''}`}>
                <button type="button" onClick={editor.undo} disabled={!editor.canUndo} title="Undo (Ctrl+Z)"
                  className="rounded-md border border-slate-600 px-2.5 py-1 text-xs text-white disabled:opacity-40 hover:border-amber-400">↶</button>
                <button type="button" onClick={editor.redo} disabled={!editor.canRedo} title="Redo (Ctrl+Shift+Z)"
                  className="rounded-md border border-slate-600 px-2.5 py-1 text-xs text-white disabled:opacity-40 hover:border-amber-400">↷</button>
                {editor.state.target?.type === 'route' && (
                  <button type="button" onClick={editor.reverse}
                    className="rounded-md border border-slate-600 px-2.5 py-1 text-xs text-white hover:border-amber-400">⇄ Reverse</button>
                )}
                <button type="button"
                  onClick={() => (editor.state.dirty ? setDiscardPrompt(true) : editor.cancel())}
                  className="rounded-md border border-slate-600 px-2.5 py-1 text-xs text-white hover:border-amber-400">Cancel</button>
                <button
                  type="button"
                  disabled={!editor.state.dirty || !editor.state.target || !canOverwrite(editor.state.target)}
                  onClick={() => void editor.save()}
                  title={editor.state.target && !canOverwrite(editor.state.target)
                    ? 'This came from someone else — use Save As'
                    : 'Overwrite the original in your library. Send to Boat is a separate step.'}
                  className="rounded-md border border-slate-600 px-2.5 py-1 text-xs text-white disabled:opacity-40 hover:border-amber-400"
                >Save to library</button>
                <button
                  type="button"
                  disabled={!editor.state.dirty}
                  onClick={() => setSaveAsName(suggestSaveAsName(editor.state.target?.name ?? 'Route', (items ?? []).map((e) => e.name)))}
                  className="rounded-md bg-sky-500 px-2.5 py-1 text-xs font-semibold text-slate-950 disabled:opacity-40"
                >Save As…</button>
              </div>
            </div>
          )}

          {/* action card */}
          {single && !editor.isEditing && (
            <div className={`absolute top-3 right-3 z-[1000] w-72 bg-slate-900 border border-slate-600 rounded-xl shadow-2xl ${cardCollapsed ? 'px-4 py-2' : 'p-4'}`}>
              <div className="flex items-start gap-2">
                <h3 className="font-bold text-white flex-1 min-w-0 truncate" style={{ color: TYPE_META[single.type].color }} title={single.name}>{single.name}</h3>
                <button
                  type="button"
                  onClick={() => setCardCollapsed((v) => !v)}
                  aria-label={cardCollapsed ? 'Expand details' : 'Collapse details'}
                  aria-expanded={!cardCollapsed}
                  className="shrink-0 -mr-1 p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-700/60"
                >
                  {cardCollapsed ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronUpIcon className="w-4 h-4" />}
                </button>
              </div>
              {!cardCollapsed && (<>
              <p className="text-xs text-slate-400 mb-3">
                {TYPE_META[single.type].label} · {metaLine(single)}
                {single.sharedByName ? ` · shared by ${single.sharedByName}` : ''}
              </p>
              {/* A recording carries a checksum of the file it came from — the
                  provenance an insurer or an investigation needs. */}
              {single.stats.provenance === 'sailed' && single.stats.source_sha256 && (
                <p className="mb-3 inline-flex items-center gap-1.5 rounded bg-emerald-400/10 px-2 py-1 font-mono text-[10px] text-emerald-300">
                  ◈ Verified recording · SHA-256 {single.stats.source_sha256.slice(0, 8)}…
                </p>
              )}
              {single.gpxFullKey && (
                <p className="mb-3 text-[11px] leading-relaxed text-slate-500">
                  Drawing {single.stats.point_count.toLocaleString()} of{' '}
                  <b className="text-slate-300">{(single.stats.original_point_count ?? single.stats.point_count).toLocaleString()}</b>{' '}
                  points — the full recording is archived and downloads in full.
                </p>
              )}
              {single.draftFit && <DraftFitBadge fit={single.draftFit} />}
              <BoatStrip
                boats={single.boats ?? []}
                onSend={(deviceId) => sendToBoat.mutate({ id: single.id, deviceId })}
              />
              {single.type === 'track' && (
                <p className="mb-3 rounded-md border-l-2 border-emerald-400 bg-emerald-400/10 px-2.5 py-2 text-[11px] leading-relaxed text-emerald-200">
                  A track records where you actually went, so it is never edited.
                  Convert it to a route to customise it — the track itself stays as recorded.
                </p>
              )}
              {single.type === 'route' && (
                <ol className="mb-3 max-h-40 overflow-y-auto space-y-0.5 pr-1">
                  {single.points.map((pt, i) => (
                    <li key={i} className="flex items-baseline gap-2 text-xs">
                      <span className="w-4 shrink-0 text-right font-mono text-slate-500">{i + 1}</span>
                      <span className="text-slate-200 truncate">{single.stats.waypoint_names?.[i] || `WP${i + 1}`}</span>
                      <span className="ml-auto shrink-0 font-mono text-[10px] text-slate-500">{pt[0].toFixed(4)}, {pt[1].toFixed(4)}</span>
                    </li>
                  ))}
                </ol>
              )}
              {scope !== 'libraries' && (<div className="flex flex-wrap gap-1.5">
                <button onClick={() => void downloadSelection('simplified')} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-600 text-xs text-white hover:border-amber-400">
                  <ArrowDownTrayIcon className="w-3.5 h-3.5" /> GPX
                </button>
                {single.gpxFullKey && (
                  <button onClick={() => void downloadSelection('original')}
                    title="The full-resolution recording"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-emerald-500/60 text-xs text-emerald-300 hover:border-emerald-400">
                    <ArrowDownTrayIcon className="w-3.5 h-3.5" /> Original
                  </button>
                )}
                {scope === 'mine' && (
                  <button onClick={() => setShareTarget(single)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-600 text-xs text-white hover:border-amber-400">
                    <ShareIcon className="w-3.5 h-3.5" /> Share
                  </button>
                )}
                <button onClick={() => { setSendTarget(single); setSendDeviceId(null); }} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-600 text-xs text-white hover:border-amber-400">
                  <PaperAirplaneIcon className="w-3.5 h-3.5" /> Send to Boat
                </button>
                {scope === 'shared' && (
                  <button onClick={() => saveCopy.mutate(single.id)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-600 text-xs text-white hover:border-amber-400">
                    <BookmarkIcon className="w-3.5 h-3.5" /> Save to my library
                  </button>
                )}
                {isEditable(single) && (
                  <button onClick={() => beginEdit(single)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-sky-500 text-xs text-sky-300 hover:bg-sky-500/10">
                    <MapIcon className="w-3.5 h-3.5" /> Edit on chart
                  </button>
                )}
                {single.type === 'track' && (
                  <button onClick={() => setConvertTrack(single)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-sky-500 text-xs text-sky-300 hover:bg-sky-500/10">
                    <MapIcon className="w-3.5 h-3.5" /> Convert to route
                  </button>
                )}
                <button onClick={() => { setRenameTarget(single); setRenameValue(single.name); }}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-600 text-xs text-white hover:border-amber-400">
                  <PencilIcon className="w-3.5 h-3.5" /> Rename
                </button>
                <button onClick={() => setDeleteTarget(single)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-slate-600 text-xs text-red-400 hover:border-red-400">
                  <TrashIcon className="w-3.5 h-3.5" /> Delete
                </button>
              </div>)}
              </>)}
            </div>
          )}
          {/* The idle hint sits where the placement overlay does, so it stands
              down while editing rather than showing through it. */}
          {selectedElements.length === 0 && !editor.isEditing && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[1000] text-xs text-slate-300 bg-slate-900/80 border border-slate-700 rounded-full px-4 py-1.5">
              Select items to preview them here · Ctrl-click to compare several
            </div>
          )}
        </div>
      </div>

      {addToLib && <AddToLibraryModal elements={addToLib} onClose={() => setAddToLib(null)} />}

      {/* Save As — a separate item that remembers its parent, not a version stack. */}
      {saveAsName !== null && (
        <div className="fixed inset-0 z-[2000] grid place-items-center bg-black/60" role="dialog" aria-modal="true">
          <div className="w-[min(420px,92vw)] rounded-2xl border border-slate-600 bg-slate-800 p-6">
            <h3 className="mb-1 text-lg font-bold text-white">Save as a new item</h3>
            <p className="mb-4 text-sm text-slate-400">
              Goes into <b>My library</b>, recorded as coming from “{editor.state.target?.name}”.
              The original is left exactly as it was.
            </p>
            <input
              autoFocus value={saveAsName} onChange={(e) => setSaveAsName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && saveAsName.trim()) { const n = saveAsName.trim(); setSaveAsName(null); void editor.saveAs(n); } }}
              className="mb-4 w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setSaveAsName(null)}
                className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-200">Cancel</button>
              <button
                disabled={!saveAsName.trim()}
                onClick={() => { const n = saveAsName.trim(); setSaveAsName(null); void editor.saveAs(n); }}
                className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50"
              >Save As</button>
            </div>
          </div>
        </div>
      )}

      {/* Waypoint rename — VN's "Edit waypoint". */}
      {wptRename && (
        <div className="fixed inset-0 z-[2000] grid place-items-center bg-black/60" role="dialog" aria-modal="true">
          <div className="w-[min(420px,92vw)] rounded-2xl border border-slate-600 bg-slate-800 p-6">
            <h3 className="mb-4 text-lg font-bold text-white">Edit waypoint</h3>
            <input
              autoFocus value={wptRename.value}
              onChange={(e) => setWptRename({ ...wptRename, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && wptRename.value.trim()) {
                  editor.renamePoint(wptRename.index, wptRename.value.trim());
                  setWptRename(null);
                }
              }}
              className="mb-4 w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setWptRename(null)}
                className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-200">Cancel</button>
              <button
                disabled={!wptRename.value.trim()}
                onClick={() => { editor.renamePoint(wptRename.index, wptRename.value.trim()); setWptRename(null); }}
                className="rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50"
              >Rename</button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel discards the whole session, so it asks first. */}
      {discardPrompt && (
        <div className="fixed inset-0 z-[2000] grid place-items-center bg-black/60" role="dialog" aria-modal="true">
          <div className="w-[min(420px,92vw)] rounded-2xl border border-slate-600 bg-slate-800 p-6">
            <h3 className="mb-1 text-lg font-bold text-white">Discard your changes?</h3>
            <p className="mb-4 text-sm text-slate-400">
              Everything you have done in this editing session is thrown away, including your undo history.
              “{editor.state.target?.name}” goes back to how it was saved.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDiscardPrompt(false)}
                className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-200">Keep editing</button>
              <button onClick={() => { setDiscardPrompt(false); editor.cancel(); }}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white">Discard</button>
            </div>
          </div>
        </div>
      )}

      {convertTrack && (
        <ConvertTrackModal
          track={convertTrack}
          onCancel={() => setConvertTrack(null)}
          onConvert={async (name: string, points: LatLon[]) => {
            const created = await api.navDraw({
              type: 'route', name, points,
              waypointNames: points.map((_, i) => `${name} - WP${i + 1}`),
              sourceId: convertTrack.id,
            });
            setConvertTrack(null);
            revealCreated(created);
            addToast({ type: 'success', title: 'Converted', message: `“${name}” is now editable as a route` });
          }}
        />
      )}

      {/* your boat */}
      {boatCardOpen && (
        <div className="fixed inset-0 z-[2000] bg-black/60 grid place-items-center" onClick={(e) => { if (e.target === e.currentTarget) setBoatCardOpen(false); }}>
          <div className="w-[min(460px,92vw)] bg-slate-800 border border-slate-600 rounded-2xl p-6">
            <h3 className="font-bold text-white text-lg mb-1">Your boat</h3>
            <p className="text-sm text-slate-400 mb-4">
              A passage is only as useful as the boat that made it. Declare yours once and every shared route says
              whether a boat like yours has actually been through.
            </p>
            <YourBoatCard
              shared={(items ?? []).concat(libPreview)}
              onClose={() => setBoatCardOpen(false)}
            />
          </div>
        </div>
      )}

      {/* share modal */}
      {shareTarget && (
        <div className="fixed inset-0 z-[2000] bg-black/60 grid place-items-center" onClick={(e) => { if (e.target === e.currentTarget) setShareTarget(null); }}>
          <div className="w-[min(420px,92vw)] bg-slate-800 border border-slate-600 rounded-2xl p-6">
            <h3 className="font-bold text-white text-lg mb-1">Share “{shareTarget.name}”</h3>
            <p className="text-sm text-slate-400 mb-4">They’ll get their own copy in <b>Shared with me</b>, attributed to you.</p>
            <input type="email" autoFocus value={shareEmail} onChange={(e) => setShareEmail(e.target.value)}
              placeholder="Their MyPolaris account email"
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 mb-3"
              onKeyDown={(e) => { if (e.key === 'Enter' && shareEmail) share.mutate({ id: shareTarget.id, email: shareEmail }); }} />
            {share.isError && (
              <p className="text-sm text-red-400 mb-3">
                {(share.error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Share failed'}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setShareTarget(null)} className="px-4 py-2 rounded-lg border border-slate-600 text-sm text-slate-300">Cancel</button>
              <button onClick={() => share.mutate({ id: shareTarget.id, email: shareEmail })} disabled={!shareEmail || share.isPending}
                className="px-4 py-2 rounded-lg bg-amber-400 text-slate-900 text-sm font-bold disabled:opacity-50">
                {share.isPending ? 'Sharing…' : 'Share'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* send-to-boat modal */}
      {sendTarget && (
        <div className="fixed inset-0 z-[2000] bg-black/60 grid place-items-center" onClick={(e) => { if (e.target === e.currentTarget) setSendTarget(null); }}>
          <div className="w-[min(460px,92vw)] bg-slate-800 border border-slate-600 rounded-2xl p-6">
            <h3 className="font-bold text-white text-lg mb-1">Send to Boat</h3>
            <p className="text-sm text-slate-400 mb-4">
              Deliver a copy of <b className="text-slate-200">“{sendTarget.name}”</b> to one of your Scouts. It arrives hidden and inactive — nothing on the chart changes until the skipper shows it.
            </p>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2">Choose a Scout</div>
            <div className="flex flex-col gap-2 mb-4">
              {myDevices.length === 0 && (
                <p className="text-sm text-slate-500">No Scouts are registered to your account yet.</p>
              )}
              {myDevices.map((d) => {
                const online = isOnline(d.last_seen_at as unknown as string | null);
                const sel = sendDeviceId === d.id;
                return (
                  <button key={d.id} type="button" onClick={() => setSendDeviceId(d.id)}
                    className={`flex items-center gap-3 text-left rounded-xl border px-4 py-3 transition-colors ${sel ? 'border-amber-400 bg-amber-400/5' : 'border-slate-700 hover:border-slate-500'}`}>
                    <span className={`w-4 h-4 rounded-full border-2 grid place-items-center ${sel ? 'border-amber-400' : 'border-slate-500'}`}>
                      {sel && <span className="w-2 h-2 rounded-full bg-amber-400" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-white truncate">{d.name || d.serial_number}</span>
                      <span className={`block text-xs ${online ? 'text-emerald-400' : 'text-slate-400'}`}>
                        {online ? '● Online now' : '○ Offline — will deliver when she next checks in'}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            {sendToBoat.isError && (
              <p className="text-sm text-red-400 mb-3">
                {(sendToBoat.error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Send failed'}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setSendTarget(null)} className="px-4 py-2 rounded-lg border border-slate-600 text-sm text-slate-300">Cancel</button>
              <button onClick={() => sendDeviceId && sendToBoat.mutate({ id: sendTarget.id, deviceId: sendDeviceId })}
                disabled={!sendDeviceId || sendToBoat.isPending}
                className="px-4 py-2 rounded-lg bg-amber-400 text-slate-900 text-sm font-bold disabled:opacity-50">
                {sendToBoat.isPending ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* rename modal */}
      {renameTarget && (
        <div className="fixed inset-0 z-[2000] bg-black/60 grid place-items-center" onClick={(e) => { if (e.target === e.currentTarget) setRenameTarget(null); }}>
          <div className="w-[min(420px,92vw)] bg-slate-800 border border-slate-600 rounded-2xl p-6">
            <h3 className="font-bold text-white text-lg mb-1">Rename</h3>
            <p className="text-sm text-slate-400 mb-4">{TYPE_META[renameTarget.type].label} · currently “{renameTarget.name}”</p>
            <input type="text" autoFocus value={renameValue} maxLength={200} onChange={(e) => setRenameValue(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 mb-3"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && renameValue.trim()) rename.mutate({ id: renameTarget.id, name: renameValue.trim() });
                if (e.key === 'Escape') setRenameTarget(null);
              }} />
            {rename.isError && (
              <p className="text-sm text-red-400 mb-3">
                {(rename.error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Rename failed'}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setRenameTarget(null)} className="px-4 py-2 rounded-lg border border-slate-600 text-sm text-slate-300">Cancel</button>
              <button onClick={() => rename.mutate({ id: renameTarget.id, name: renameValue.trim() })}
                disabled={!renameValue.trim() || renameValue.trim() === renameTarget.name || rename.isPending}
                className="px-4 py-2 rounded-lg bg-amber-400 text-slate-900 text-sm font-bold disabled:opacity-50">
                {rename.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* delete confirm modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-[2000] bg-black/60 grid place-items-center" onClick={(e) => { if (e.target === e.currentTarget) setDeleteTarget(null); }}>
          <div className="w-[min(420px,92vw)] bg-slate-800 border border-slate-600 rounded-2xl p-6">
            <h3 className="font-bold text-white text-lg mb-1">Delete “{deleteTarget.name}”?</h3>
            <p className="text-sm text-slate-400 mb-4">
              This removes it from your MyPolaris library. It can’t be undone — copies already on your Scout or shared with others are not affected.
            </p>
            {del.isError && (
              <p className="text-sm text-red-400 mb-3">
                {(del.error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Delete failed'}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 rounded-lg border border-slate-600 text-sm text-slate-300">Keep</button>
              <button onClick={() => del.mutate(deleteTarget.id)} disabled={del.isPending}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50">
                {del.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
